import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchAlerts, type RealtimeAlert } from '@/services/gtfs-realtime';
import { useFavouriteStops, useSavedTrips } from '@/store/favourites';
import { canPoll } from '@/services/polling';
import './AlertsScreen.css';

function fmtDate(epochSec: number): string {
  if (!epochSec) return '';
  return new Date(epochSec * 1000).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function AlertsScreen() {
  const [alerts, setAlerts] = useState<RealtimeAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterMyStops, setFilterMyStops] = useState(true);
  const { stops } = useFavouriteStops();
  const { trips } = useSavedTrips();

  const savedStopIds = useMemo(() => {
    const ids = new Set<string>();
    stops.forEach(s => ids.add(s.id));
    trips.forEach(t => { ids.add(t.originId); ids.add(t.destId); });
    return ids;
  }, [stops, trips]);

  const loadAlerts = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await fetchAlerts('all');
      data.sort((a, b) => {
        const aStart = a.active_periods?.[0]?.start || 0;
        const bStart = b.active_periods?.[0]?.start || 0;
        return bStart - aStart;
      });
      setAlerts(data);
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  // Poll for new alerts while the tab is visible and online.
  useEffect(() => {
    const id = setInterval(() => {
      if (canPoll()) loadAlerts();
    }, 60_000);
    return () => clearInterval(id);
  }, [loadAlerts]);

  const filtered = filterMyStops && savedStopIds.size > 0
    ? alerts.filter(a => a.informed_stops.length > 0 && a.informed_stops.some(s => savedStopIds.has(s)))
    : alerts;

  return (
    <main className="page-container">
      <h1 className="page-title">Service Alerts</h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button className="refresh-btn" onClick={loadAlerts} disabled={loading}>
          {loading ? 'Loading...' : '🔄 Refresh'}
        </button>
        {savedStopIds.size > 0 && (
          <button className={`route-pill${filterMyStops ? ' route-pill--active' : ''}`} onClick={() => setFilterMyStops(!filterMyStops)}>
            {filterMyStops ? '📍 My stops only' : `🌐 All (${alerts.length})`}
          </button>
        )}
      </div>

      {savedStopIds.size === 0 && !loading && (
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
          💡 Add favourite stops or saved trips to filter alerts for your routes.
        </div>
      )}

      {error && <div className="alerts-error">{error}</div>}

      {!loading && filtered.length === 0 && !error && (
        <div className="placeholder-screen">
          <span className="icon">✅</span>
          <p className="label">{filterMyStops ? 'No alerts affecting your saved stops' : 'No active service alerts'}</p>
        </div>
      )}

      {filtered.map((alert) => {
        const firstPeriod = alert.active_periods?.[0];
        const isActive = firstPeriod && (firstPeriod.end === 0 || firstPeriod.end * 1000 > Date.now());
        const activeColor = isActive ? '#D81E34' : '#9E9E9E';
        const startStr = firstPeriod?.start ? fmtDate(firstPeriod.start) : '';
        const endStr = firstPeriod?.end && firstPeriod.end > 0 ? fmtDate(firstPeriod.end) : '';
        return (
          <div key={alert.id} className="card alert-card" style={{ borderLeftColor: activeColor }}>
            <div className="alert-header">
              <span className="alert-time">{startStr ? `Active from ${startStr}` : ''}{endStr ? ` – ${endStr}` : ''}</span>
              <span className="alert-severity" style={{ background: isActive ? '#FFCDD220' : undefined, color: activeColor }}>
                {isActive ? '🔴 ACTIVE' : 'ℹ INFO'}
              </span>
            </div>
            <h3 className="alert-title">{alert.header_text}</h3>
            <p className="alert-description">{alert.description_text}</p>
            {alert.informed_routes.length > 0 && (
              <p className="alert-meta"><strong>Routes:</strong> {alert.informed_routes.slice(0, 10).join(', ')}{alert.informed_routes.length > 10 ? ` +${alert.informed_routes.length - 10} more` : ''}</p>
            )}
            {alert.informed_stops.length > 0 && (
              <p className="alert-meta"><strong>Stops:</strong> {alert.informed_stops.length} affected</p>
            )}
          </div>
        );
      })}

      {filterMyStops && savedStopIds.size > 0 && filtered.length < alerts.length && (
        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', textAlign: 'center', marginTop: 12 }}>
          Showing {filtered.length} of {alerts.length} alerts • <button style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setFilterMyStops(false)}>Show all</button>
        </p>
      )}
    </main>
  );
}


